import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import PrinterErrorModal from "../alerts/PrinterErrorModal";
import { dispatchSetShowPrinterAlert } from "../../utils/alerts";
import {
  initializePrintService,
  websocketPrintClient,
} from "../../utils/printService";

const PERSISTENT_RECONNECT_INTERVAL = 5000;
const PRINTER_IGNORE_KEY = "slapie_printer_ignored";

export default function PrintersManager() {
  const user = useSelector((state) => state.auth.user);
  const showPrinterAlert = useSelector(
    (state) => state.alerts.showPrinterAlert
  );

  const [isConnected, setIsConnected] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [reconnectAttempt, setReconnectAttempt] = useState(0);
  const [persistentReconnectTimer, setPersistentReconnectTimer] =
    useState(null);
  const [isPrinterIgnored, setIsPrinterIgnored] = useState(() => {
    const ignoredValue = localStorage.getItem(PRINTER_IGNORE_KEY);
    return ignoredValue === "true";
  });

  useEffect(() => {
    console.log(
      "PrintersManager - showPrinterAlert state changed:",
      showPrinterAlert
    );
  }, [showPrinterAlert]);

  useEffect(() => {
    const isPrinterCurrentlyIgnored = isPrinterIgnored;

    if (isPrinterCurrentlyIgnored) {
      console.log("Printer connections are ignored - skipping initialization");
      return;
    }

    const hasUser = Boolean(user);

    if (hasUser) {
      handleInitializeWebSocket();
    }

    if (!hasUser) {
      handleDisconnectWebSocket();
    }

    return () => {
      const shouldDisconnect = !user;

      if (shouldDisconnect) {
        handleDisconnectWebSocket();
      }

      const hasTimer = Boolean(persistentReconnectTimer);

      if (hasTimer) {
        clearInterval(persistentReconnectTimer);
      }
    };
  }, [user, isPrinterIgnored]);

  useEffect(() => {
    const isPrinterCurrentlyIgnored = isPrinterIgnored;

    if (isPrinterCurrentlyIgnored) {
      return;
    }

    const wasReconnecting = isReconnecting;
    const isNowConnected = isConnected;
    const alertIsShowing = showPrinterAlert;
    const shouldCloseAlert =
      wasReconnecting && isNowConnected && alertIsShowing;

    if (shouldCloseAlert) {
      console.log("Connection restored - auto-closing printer alert");
      dispatchSetShowPrinterAlert(false);
      setIsReconnecting(false);
    }
  }, [isConnected, showPrinterAlert, isReconnecting, isPrinterIgnored]);

  useEffect(() => {
    const isPrinterCurrentlyIgnored = isPrinterIgnored;

    if (isPrinterCurrentlyIgnored) {
      const hasTimer = Boolean(persistentReconnectTimer);

      if (hasTimer) {
        clearInterval(persistentReconnectTimer);
        setPersistentReconnectTimer(null);
      }

      return;
    }

    const hasReachedMaxAttempts =
      reconnectAttempt >= websocketPrintClient.maxReconnectAttempts;
    const shouldStartPersistentReconnect =
      user &&
      !isConnected &&
      !isReconnecting &&
      hasReachedMaxAttempts &&
      !persistentReconnectTimer;

    if (shouldStartPersistentReconnect) {
      startPersistentReconnection();
    }

    const shouldStopPersistentReconnect =
      isConnected && persistentReconnectTimer;

    if (shouldStopPersistentReconnect) {
      console.log("Stopping persistent reconnection - connection restored");
      clearInterval(persistentReconnectTimer);
      setPersistentReconnectTimer(null);
    }

    return () => {
      const hasTimer = Boolean(persistentReconnectTimer);

      if (hasTimer) {
        clearInterval(persistentReconnectTimer);
      }
    };
  }, [
    isConnected,
    isReconnecting,
    reconnectAttempt,
    user,
    persistentReconnectTimer,
    isPrinterIgnored,
  ]);

  const handleInitializeWebSocket = async () => {
    const isPrinterCurrentlyIgnored = isPrinterIgnored;

    if (isPrinterCurrentlyIgnored) {
      console.log("Printer ignored - skipping WebSocket initialization");
      return;
    }

    const isAlreadyConnected =
      isConnected && websocketPrintClient.socket?.readyState === WebSocket.OPEN;

    if (isAlreadyConnected) {
      console.log("WebSocket already connected, skipping initialization");
      return;
    }

    console.log("Initializing WebSocket with callbacks...");

    const callbacks = {
      onConnect: handleWebSocketConnect,
      onDisconnect: handleWebSocketDisconnect,
      onError: handleWebSocketError,
      onPrintSuccess: handlePrintSuccess,
      onPrintError: handlePrintError,
      onLog: handleWebSocketLog,
    };

    websocketPrintClient.setCallbacks(callbacks);
    console.log("Callbacks registered successfully");

    try {
      await initializePrintService();

      console.log("WebSocket initialization completed successfully");
      setIsConnected(true);
      setIsReconnecting(false);
    } catch (error) {
      console.error("Failed to initialize WebSocket:", error);

      setIsConnected(false);

      const shouldShowAlert = Boolean(user);

      if (shouldShowAlert) {
        console.log(
          "Initialization failed - showing alert and enabling reconnection"
        );
        setIsReconnecting(true);
        dispatchSetShowPrinterAlert(true);
      }
    }
  };

  const handleWebSocketConnect = () => {
    console.log("WebSocket connected - closing alert modal");

    setIsConnected(true);
    setIsReconnecting(false);
    setReconnectAttempt(0);

    const hasTimer = Boolean(persistentReconnectTimer);

    if (hasTimer) {
      clearInterval(persistentReconnectTimer);
      setPersistentReconnectTimer(null);
    }

    dispatchSetShowPrinterAlert(false);
  };

  const handleWebSocketDisconnect = () => {
    console.log("WebSocket disconnected - TRIGGERING ALERT MODAL");

    const isPrinterCurrentlyIgnored = isPrinterIgnored;

    if (isPrinterCurrentlyIgnored) {
      console.log("Printer ignored - skipping alert on disconnect");
      return;
    }

    setIsConnected(false);

    const shouldReconnect = Boolean(user);

    if (shouldReconnect) {
      console.log("User exists - Will attempt to reconnect and show alert");
      setIsReconnecting(true);
      dispatchSetShowPrinterAlert(true);
      console.log("Alert modal state set to TRUE");
    } else {
      console.log("No user - skipping reconnection and alert");
    }
  };

  const handleWebSocketError = (error) => {
    console.error(
      "WebSocket Print Service error - TRIGGERING ALERT MODAL:",
      error
    );

    const isPrinterCurrentlyIgnored = isPrinterIgnored;

    if (isPrinterCurrentlyIgnored) {
      console.log("Printer ignored - skipping alert on error");
      return;
    }

    const shouldShowAlert = Boolean(user);

    if (shouldShowAlert) {
      console.log("Showing alert due to WebSocket error");
      setIsReconnecting(true);
      dispatchSetShowPrinterAlert(true);
      console.log("Alert modal state set to TRUE due to error");
    }
  };

  const handlePrintSuccess = (data) => {
    console.log("Print job completed successfully:", data);
  };

  const handlePrintError = (data) => {
    console.error("Print job failed:", data);

    const isPrinterCurrentlyIgnored = isPrinterIgnored;

    if (isPrinterCurrentlyIgnored) {
      console.log("Printer ignored - skipping alert on print error");
      return;
    }

    dispatchSetShowPrinterAlert(true);
  };

  const handleWebSocketLog = (message, level) => {
    const logLevel = level || "log";
    console[logLevel](`Print Service: ${message}`);

    const isReconnectAttemptMessage = message.includes(
      "Attempting to reconnect"
    );

    if (isReconnectAttemptMessage) {
      const attemptMatch = message.match(/attempt (\d+)\//);
      const hasMatch = Boolean(attemptMatch);

      if (hasMatch) {
        const attemptNumber = parseInt(attemptMatch[1]);
        setReconnectAttempt(attemptNumber);
      }
    }

    const isMaxAttemptsMessage = message.includes("Maximum reconnect attempts");

    if (isMaxAttemptsMessage) {
      setIsReconnecting(false);
    }
  };

  const startPersistentReconnection = () => {
    const isPrinterCurrentlyIgnored = isPrinterIgnored;

    if (isPrinterCurrentlyIgnored) {
      console.log("Printer ignored - skipping persistent reconnection");
      return;
    }

    const hasExistingTimer = Boolean(persistentReconnectTimer);

    if (hasExistingTimer) {
      clearInterval(persistentReconnectTimer);
    }

    console.log("Starting persistent reconnection attempts every 5 seconds...");

    const timerId = setInterval(async () => {
      const isPrinterCurrentlyIgnored = isPrinterIgnored;

      if (isPrinterCurrentlyIgnored) {
        console.log("Printer ignored - stopping persistent reconnection");
        clearInterval(timerId);
        setPersistentReconnectTimer(null);
        return;
      }

      const shouldAttempt = Boolean(user) && !isConnected;

      if (!shouldAttempt) {
        clearInterval(timerId);
        setPersistentReconnectTimer(null);
        return;
      }

      console.log("Persistent reconnection attempt...");

      try {
        websocketPrintClient.reconnectAttempts = 0;

        await websocketPrintClient.connect({
          host: websocketPrintClient.host,
          port: websocketPrintClient.defaultPort,
        });
      } catch (error) {
        console.log("Persistent reconnection failed, will retry in 5s");
      }
    }, PERSISTENT_RECONNECT_INTERVAL);

    setPersistentReconnectTimer(timerId);
  };

  const handleDisconnectWebSocket = () => {
    setIsConnected(false);
    setIsReconnecting(false);
    setReconnectAttempt(0);

    const hasTimer = Boolean(persistentReconnectTimer);

    if (hasTimer) {
      clearInterval(persistentReconnectTimer);
      setPersistentReconnectTimer(null);
    }

    websocketPrintClient.disconnect();
    dispatchSetShowPrinterAlert(false);
  };

  const handleClose = () => {
    const canClose = !isReconnecting;

    if (!canClose) {
      return;
    }

    dispatchSetShowPrinterAlert(false);
  };

  const handleManualReconnect = async () => {
    console.log("Manual reconnection triggered by user");

    const isPrinterCurrentlyIgnored = isPrinterIgnored;

    if (isPrinterCurrentlyIgnored) {
      console.log("Printer ignored - skipping manual reconnection");
      return;
    }

    try {
      websocketPrintClient.reconnectAttempts = 0;
      setReconnectAttempt(0);
      setIsReconnecting(true);

      await websocketPrintClient.connect({
        host: websocketPrintClient.host,
        port: websocketPrintClient.defaultPort,
      });
    } catch (error) {
      console.error("Manual reconnection failed:", error);
    }
  };

  const handleIgnorePrinter = () => {
    console.log("User chose to ignore printer connections");

    localStorage.setItem(PRINTER_IGNORE_KEY, "true");
    setIsPrinterIgnored(true);

    handleDisconnectWebSocket();
    dispatchSetShowPrinterAlert(false);

    const hasTimer = Boolean(persistentReconnectTimer);

    if (hasTimer) {
      clearInterval(persistentReconnectTimer);
      setPersistentReconnectTimer(null);
    }
  };

  const getErrorMessage = () => {
    if (isReconnecting) {
      const hasAttemptNumber = reconnectAttempt > 0;

      if (hasAttemptNumber) {
        const maxAttempts = websocketPrintClient.maxReconnectAttempts;
        return `Tentando reconectar à impressora... (Tentativa ${reconnectAttempt}/${maxAttempts})`;
      }

      return "Conexão com a impressora perdida. Reconectando...";
    }

    const hasMaxAttemptsReached =
      reconnectAttempt >= websocketPrintClient.maxReconnectAttempts;

    if (hasMaxAttemptsReached) {
      const hasPersistentTimer = Boolean(persistentReconnectTimer);

      if (hasPersistentTimer) {
        return "Verificando conexão a cada 5 segundos. Abra o aplicativo Slapie no Windows.";
      }

      return "A reconexão deu errado. Abra o aplicativo Slapie no Windows.";
    }

    return "Conexão com a impressora perdida. Verifique se o app Slapie está aberto.";
  };

  const shouldShowModal = showPrinterAlert && !isPrinterIgnored;
  const errorMessage = getErrorMessage();

  return (
    <>
      <PrinterErrorModal
        open={shouldShowModal}
        onClose={handleClose}
        errorMessage={errorMessage}
        onManualReconnect={handleManualReconnect}
        onIgnorePrinter={handleIgnorePrinter}
      />
    </>
  );
}
